import { Dialog, Flex, Spinner, Text } from '@applyboard/crystal-ui'
import styled from '@emotion/styled'
import { ChevronRightOutlineIcon } from '@applyboard/ui-icons'
import { format, isValid } from 'date-fns'

type PreviousApplicationInfo = {
  programName: string
  intakeTermName: string
  submittedDate: string
  humanReadableId: string
}

type PrefillApplicationDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  previousApplication: PreviousApplicationInfo
  onPrefill: () => void
  onStartFresh: () => void
  isLoading?: boolean
}

function formatSubmittedDate(dateString: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (!isValid(date)) return ''
  return format(date, 'MMMM d, yyyy')
}

export function PrefillApplicationDialog(props: PrefillApplicationDialogProps) {
  const formattedDate = formatSubmittedDate(props.previousApplication.submittedDate)

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.isLoading ? () => {} : props.onOpenChange}
      size="md"
    >
      <Dialog.Heading>Start New Application</Dialog.Heading>
      <Dialog.Content>
        {props.isLoading ? (
          <Flex direction="column" align="center" gap={4} py={6}>
            <Spinner size="lg" />
            <Text variant="bodyM" contrast="mid">
              Copying your information...
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" gap={6}>
            <Text variant="bodyM" contrast="mid">
              Would you like to use the details from your application{' '}
              <strong>
                {props.previousApplication.humanReadableId} -{' '}
                {props.previousApplication.programName} - {props.previousApplication.intakeTermName}
              </strong>
              {formattedDate ? (
                <>
                  {' '}
                  submitted on <strong>{formattedDate}</strong>
                </>
              ) : null}
              ?
            </Text>

            <Flex direction="column" gap={3}>
              <OptionCard
                title="Pre-fill from previous"
                subtitle={`Use data from ${props.previousApplication.humanReadableId}`}
                onClick={props.onPrefill}
              />
              <OptionCard
                title="Start from scratch"
                subtitle="Enter all details manually"
                onClick={() => {
                  props.onOpenChange(false)
                  props.onStartFresh()
                }}
              />
            </Flex>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog>
  )
}

type OptionCardProps = {
  title: string
  subtitle: string
  onClick: () => void
}

function OptionCard(props: OptionCardProps) {
  return (
    <OptionCardButton type="button" onClick={props.onClick}>
      <Flex align="center" gap={4} wrap={false} grow={1}>
        <Flex direction="column" gap={1} grow={1}>
          <Text variant="bodyM">
            <strong>{props.title}</strong>
          </Text>
          <Text variant="bodyS" contrast="mid">
            {props.subtitle}
          </Text>
        </Flex>
        <ChevronRightOutlineIcon size="md" intent="primary" />
      </Flex>
    </OptionCardButton>
  )
}

const OptionCardButton = styled.button(({ theme }) => ({
  display: 'flex',
  width: '100%',
  padding: 16,
  border: `1px solid ${theme.colors.borderRegular}`,
  borderRadius: 12,
  backgroundColor: theme.colors.bgRegular,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    borderColor: theme.colors.actionPrimary,
    boxShadow: `0 0 0 1px ${theme.colors.actionPrimary}`,
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: theme.colors.actionPrimary,
    boxShadow: `0 0 0 2px ${theme.colors.actionPrimary}`,
  },
}))
